/**
 * WebSocket地址生成工具
 * 根据当前域名和环境自动生成WebSocket服务器地址
 */

/**
 * 根据当前页面协议生成WebSocket协议
 */
function getWebSocketProtocol(): 'ws:' | 'wss:' {
  const protocol = window.location.protocol
  return protocol === 'https:' ? 'wss:' : 'ws:'
}

function ensureTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`
}

function resolveConfiguredWebSocketUrl(url: string): string {
  if (url.startsWith('/')) {
    return ensureTrailingSlash(`${getWebSocketProtocol()}//${window.location.host}${url}`)
  }

  return ensureTrailingSlash(url)
}

/**
 * 获取WebSocket端口
 * @param isDev 是否为开发环境
 * @param isWebRTC 是否为WebRTC信令地址
 */
function getWebSocketPort(isDev: boolean, isWebRTC: boolean = false): string {
  if (isDev) {
    // 开发环境使用配置的端口
    return isWebRTC ? '8080' : '8081'  // 协议网关端口
  }

  // 生产环境由Nginx转发，不需要显式指定后端端口
  return ''
}

/**
 * 生成WebSocket主连接地址
 * @param isDev 是否为开发环境
 * @returns WebSocket URL
 */
export function generateWebSocketUrl(isDev: boolean = false): string {
  if (isDev) {
    // 开发环境优先使用 .env 配置，否则跟随当前页面主机
    const configuredUrl = import.meta.env.VITE_WS_URL
    if (configuredUrl) {
      return resolveConfiguredWebSocketUrl(configuredUrl)
    }

    const protocol = getWebSocketProtocol()
    const hostname = window.location.hostname || 'localhost'
    return `${protocol}//${hostname}:8081/ws/afsa/v1/`
  }

  // 生产环境根据当前域名生成
  const protocol = getWebSocketProtocol()
  const hostname = window.location.hostname
  const port = getWebSocketPort(isDev, false)
  const portPart = port ? `:${port}` : ''

  return `${protocol}//${hostname}${portPart}/ws/afsa/v1/`
}

/**
 * 归一化持久化的 WebSocket 地址，避免生产环境复用旧的网关直连端口。
 */
export function normalizeWebSocketUrl(url: string, isDev: boolean = false): string {
  if (!url) {
    return generateWebSocketUrl(isDev)
  }

  const fallbackUrl = generateWebSocketUrl(isDev)

  try {
    const parsedUrl = new URL(url)
    const isCurrentHost = parsedUrl.hostname === window.location.hostname
    const isGatewayPort = parsedUrl.port === '8081' || parsedUrl.port === '8082'
    const isAfsaSocketPath = parsedUrl.pathname.replace(/\/$/, '') === '/ws/afsa/v1'

    // 开发环境迁移历史联调地址，避免持久化配置继续连接已失效的固定网段。
    const isLegacyDevelopmentHost = ['10.9.5.147', '192.168.66.172'].includes(parsedUrl.hostname)
    const isLocalDevelopmentGateway = ['localhost', '127.0.0.1', '[::1]'].includes(parsedUrl.hostname)
    if (isDev && isAfsaSocketPath && (isLegacyDevelopmentHost || (isLocalDevelopmentGateway && isGatewayPort))) {
      return fallbackUrl
    }

    if (!isDev && isCurrentHost && isGatewayPort && isAfsaSocketPath) {
      return `${parsedUrl.protocol}//${parsedUrl.hostname}/ws/afsa/v1/`
    }
  } catch {
    return fallbackUrl
  }

  return resolveConfiguredWebSocketUrl(url)
}

/**
 * 生成WebRTC信令地址
 * @param isDev 是否为开发环境
 * @returns WebRTC Signaling WebSocket URL
 */
export function generateWebRTCSignalingUrl(isDev: boolean = false): string {
  if (isDev) {
    // 开发环境跟随当前页面主机，避免依赖固定联调网段地址
    const protocol = getWebSocketProtocol()
    const hostname = window.location.hostname || 'localhost'
    return `${protocol}//${hostname}:8080/ws/webrtc/signaling`
  }

  // 生产环境根据当前域名生成
  const protocol = getWebSocketProtocol()
  const hostname = window.location.hostname
  const port = getWebSocketPort(isDev, true)
  const portPart = port ? `:${port}` : ''

  return `${protocol}//${hostname}${portPart}/ws/webrtc/signaling`
}

/**
 * 验证WebSocket URL格式
 * @param url WebSocket URL
 * @returns 是否有效
 */
export function validateWebSocketUrl(url: string): boolean {
  if (!url) {
    return false
  }

  // 必须以ws://或wss://开头
  if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
    return false
  }

  // 基本URL格式验证
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * 获取环境类型
 */
export function getEnvironmentType(): 'development' | 'production' | 'test' {
  // @ts-ignore - import.meta.env在Vite中可用
  if (import.meta.env.DEV) {
    return 'development'
  }

  // @ts-ignore
  if (import.meta.env.VITE_ENV === 'test') {
    return 'test'
  }

  return 'production'
}

/**
 * 获取默认WebSocket配置
 */
export function getDefaultWebSocketConfig() {
  const env = getEnvironmentType()
  const isDev = env === 'development'

  return {
    url: generateWebSocketUrl(isDev),
    webrtcSignalingUrl: generateWebRTCSignalingUrl(isDev),
    autoConnect: env === 'production',
    multiConnection: false,
  }
}
